import Navbar from '@/components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Microscope, FlaskConical, Dna, Search, Cpu, Sparkles } from 'lucide-react';

const Section = ({ icon: Icon, title, children, accent = 'blue' }) => {
  const accentMap = {
    blue: 'text-blue-600 bg-blue-50',
    purple: 'text-purple-600 bg-purple-50',
    emerald: 'text-emerald-600 bg-emerald-50',
    amber: 'text-amber-600 bg-amber-50',
  };
  return (
    <Card className="overflow-hidden border-gray-200">
      <CardHeader className="bg-gray-50 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accentMap[accent]}`}>
            <Icon className="w-5 h-5" />
          </div>
          <CardTitle className="text-xl text-gray-900">{title}</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="pt-6 text-gray-700 leading-relaxed space-y-4">
        {children}
      </CardContent>
    </Card>
  );
};

const SubItem = ({ title, children }) => (
  <div>
    <h4 className="font-semibold text-gray-900">{title}</h4>
    <p className="text-gray-700">{children}</p>
  </div>
);

const InfoPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <header className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-100 text-blue-700 text-sm font-medium mb-4">
            <Sparkles className="w-4 h-4" />
            OliSeq Comprehensive Guide
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-3" data-testid="info-page-title">
            Oliseq: Comprehensive Guide to PCR Technologies
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Advanced Solutions for Gene Expression, Genotyping, and Custom Assay Design
          </p>
        </header>

        <div className="space-y-8">
          {/* 1. Core PCR Technologies Overview */}
          <Section icon={Microscope} title="1. Core PCR Technologies Overview" accent="blue">
            <p>
              At Oliseq, we leverage the most advanced nucleic acid amplification technologies
              to provide highly sensitive and specific solutions for researchers and clinicians.
              Understanding the distinction between Real-Time PCR and Digital PCR, as well as
              the underlying probe chemistry, is crucial for selecting the right analytical tool.
            </p>
            <SubItem title="Real-Time PCR (qPCR) vs. Digital PCR (dPCR)">
              <span className="font-medium text-gray-900">Real-Time PCR (qPCR):</span>{' '}
              Measures the accumulation of DNA during the exponential phase of the reaction
              using fluorescent dyes or probes. It is highly efficient for relative quantification,
              gene expression studies, and pathogen detection. It relies on standard curves for
              absolute quantification.
            </SubItem>
            <SubItem title="Digital PCR (dPCR)">
              A paradigm shift in quantification. dPCR partitions a single sample into thousands
              of individual micro-reactions (droplets or physical partitions). By performing
              endpoint PCR in each partition and analyzing the positive vs. negative ratio using
              Poisson statistics, dPCR allows for absolute quantification of target nucleic acids
              without the need for a standard curve. It provides unmatched precision for rare
              mutation detection and low-copy-number targets.
            </SubItem>
          </Section>

          {/* 2. Advanced Primer & Probe Technologies */}
          <Section icon={Dna} title="2. Advanced Primer & Probe Technologies" accent="purple">
            <p>
              The core of our diagnostic and research services relies on highly specific
              oligonucleotide probes. While intercalating dyes bind to any double-stranded DNA,
              probe-based assays offer target-specific fluorescence, enabling multiplexing and
              reducing false positives.
            </p>
            <SubItem title="How Hydrolysis Probes (TaqMan™) Work">
              A dual-labeled hydrolysis probe is designed to bind specifically to the target DNA
              between the forward and reverse primers. The probe is labeled with a fluorescent
              reporter (F) at the 5&apos; end and a quencher (Q) at the 3&apos; end. When intact,
              FRET (Fluorescence Resonance Energy Transfer) prevents the reporter from emitting
              light. During the extension phase, the 5&apos; to 3&apos; exonuclease activity of
              Taq polymerase cleaves the probe. The reporter is separated from the quencher,
              resulting in a fluorescent signal that is directly proportional to the amount of
              PCR product generated.
            </SubItem>
          </Section>

          {/* 3. Products & Services */}
          <Section icon={FlaskConical} title="3. Products & Services" accent="emerald">
            <p>
              Oliseq offers a comprehensive suite of pre-designed and customizable molecular
              solutions to accelerate your research.
            </p>
            <SubItem title="Gene Expression">
              Accurate profiling of mRNA and lncRNA transcripts. Our optimized primer/probe sets
              provide high amplification efficiency and a broad dynamic range, perfect for
              pathway analysis, biomarker discovery, and cellular response profiling in both
              qPCR and dPCR setups.
            </SubItem>
            <SubItem title="Genotyping">
              Precision assays for the identification of Single Nucleotide Polymorphisms (SNPs)
              and insertions/deletions (Indels). We design robust assays capable of distinct
              allelic discrimination, essential for population genetics, pharmacogenomics, and
              breeding programs.
            </SubItem>
            <SubItem title="Pathogen Detection">
              Highly sensitive molecular tests designed for the rapid identification of viral,
              bacterial, and fungal agents. Utilizing multiplex-ready probe configurations, our
              assays ensure reliable pathogen load monitoring even in complex sample matrices.
            </SubItem>
            <SubItem title="miRNA Assays">
              Specialized methodologies tailored for the detection of short, mature microRNAs.
              Our miRNA assays utilize stem-loop reverse transcription primers or LNA (Locked
              Nucleic Acid) enhanced probes to overcome the challenges of short sequence length
              and high homology among miRNA families.
            </SubItem>
          </Section>

          {/* 4. Custom Design & Engineering */}
          <Section icon={Cpu} title="4. Custom Design & Engineering" accent="amber">
            <p>
              Our robust bioinformatics infrastructure allows you to tailor assays precisely to
              your experimental needs.
            </p>
            <SubItem title="PrimerDesignWizard (Custom Primer/Probe Design)">
              Our proprietary, intuitive online tool enables researchers to input their target
              sequences and instantly receive optimized, in-silico validated primer and probe
              designs. The wizard accounts for melting temperatures (Tm), secondary structures,
              and off-target homologies.
            </SubItem>
            <SubItem title="Custom Order Your Probes & Primers">
              Flexible oligonucleotide synthesis services. Select from a diverse catalog of
              fluorophores, quenchers, and chemical modifications (e.g., LNA, MGB,
              Phosphorothioate bonds) to construct bespoke probes tailored for highly specialized
              applications.
            </SubItem>
            <SubItem title="Design Your Own Probe Ready Assays">
              For researchers who already have validated sequences, this service provides a
              streamlined path from sequence to bench. Simply submit your sequences, and Oliseq
              will manufacture, formulate, and deliver a fully optimized, ready-to-use master
              mix assay.
            </SubItem>
            <SubItem title="GACE MAS Marker Assay">
              GACE (Genotyping Assay by Competitive Extension) Marker-Assisted Selection (MAS)
              is a cutting-edge endpoint PCR technology for high-throughput SNP genotyping.
              Conceptually similar to popular platforms like PACE™ or KASP™, the GACE MAS assay
              utilizes competitive allele-specific PCR combined with universal FRET cassettes.
              However, GACE utilizes uniquely modified fluorescent probes and a proprietary
              buffer chemistry. This innovation enhances binding specificity, delivers tighter
              data clustering, and offers exceptional tolerance to impurities commonly found in
              crude plant or animal DNA extracts. It is the ideal, cost-effective solution for
              large-scale agricultural breeding and genetic screening.
            </SubItem>
          </Section>
        </div>

        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-2 text-gray-600">
            <Search className="w-4 h-4" />
            <span>Need help selecting the right technology? Visit our Contact page or explore our Tools.</span>
          </div>
        </div>
      </main>
    </div>
  );
};

export default InfoPage;
